import { FC, PropsWithChildren, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { AuthenticateResponse } from '@/dtos';
import { AppSettings } from '@/AppSettings';
import { createSentryAnonymousUser } from '@/errors';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import AuthenticatedUserContext from '@/contexts/AuthenticatedUserContext';
import IsLoading from '../layout/isLoading';
import useAuthenticationHelper from '@/hooks/useAuthenticationHelper';
import { useSWRConfig } from 'swr';



export interface IAuthenticatedUserProviderProps {
    initial?: AuthenticateResponse | null;
}



export const AuthenticatedUserProvider: FC<PropsWithChildren<IAuthenticatedUserProviderProps>> = ({ initial, children }) => {
    const authenticatedUser = useAuthenticatedUser({ revalidateOnFocus: true, errorRetryCount: 0,  refreshInterval: 600000, fallbackData: initial});
    const { cache } = useSWRConfig()

    const [checkLogin, setCheckLogin] = useState(false);
    const [loginChecked, setLoginChecked] = useState(false);

    useEffect(() => {
        if (!authenticatedUser.loading && !authenticatedUser.validating && !authenticatedUser.data) {
            setCheckLogin(true);
        }
    }, [authenticatedUser.loading, authenticatedUser.validating, authenticatedUser.data, setCheckLogin]);

    useEffect(() => {
        if (!AppSettings.sentry.dsn) {
            return;
        }

        const user = authenticatedUser.data;

        if (!user?.userName) {
            Sentry.setUser(createSentryAnonymousUser());
        } else {
            Sentry.setUser({
                id: user.userName,
                username: user.userName,
                fullName: user.displayName ?? user.userName,
                authenticated: true,
            });
        }

        return () => {
            Sentry.setUser(createSentryAnonymousUser());
        };
    }, [authenticatedUser.data]);

    useAuthenticationHelper(() => { 
        for (const key of cache.keys()) {
            if (typeof key === 'string' && key.includes('Authenticate')) {
                cache.delete(key);
            }
        }
    });


    return (
        <AuthenticatedUserContext.Provider value={authenticatedUser}>
            <IsLoading name="authenticated-user" loading={authenticatedUser.loading} validating={authenticatedUser.validating}/>
            {children}
            {
                checkLogin && !loginChecked && (
                    <iframe style={{ display: 'none' }} src="/api/CheckLogin" onLoad={() => {authenticatedUser.refresh(); setLoginChecked(true); }} />
                )
            }
        </AuthenticatedUserContext.Provider>
    );
}

export default AuthenticatedUserProvider;
